import React, { useEffect, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useNavigate, NavLink } from "react-router-dom";
import { signIn } from "aws-amplify/auth";
import {
    Box,
    FormControl,
    FormLabel,
    Input,
    Link,
    Stack,
    Typography,
    formLabelClasses,
    Button,
    Sheet,
} from "@mui/joy";
import { useMutation } from "react-query";
import { useForm } from "@tanstack/react-form";
import ValidationFieldInfo from "../ValidationFieldInfo";
import { getErrorMessage } from "../../ui-components/utils";
import { ColorSchemeToggle } from "../ColorSchemeToggle/index";

interface FormElements extends HTMLFormControlsCollection {
    email: HTMLInputElement;
    password: HTMLInputElement;
}

interface SignInFormElement extends HTMLFormElement {
    readonly elements: FormElements;
}

interface SignInParams {
    email: string;
    password: string;
}

type SignInError = { type: "auth" } | { type: "userNotFound" } | { type: "general"; message: string };

const signInMutation = async ({ email, password }: SignInParams) => {
    const { isSignedIn, nextStep } = await signIn({ username: email, password });
    return { isSignedIn, nextStep };
};

export const SignIn = () => {
    const { authStatus } = useAuthenticator((context) => [context.authStatus]);
    const navigate = useNavigate();
    const [error, setError] = useState<SignInError | null>(null);

    const form = useForm({
        defaultValues: {
            email: "",
            password: "",
        },
        onSubmit: async ({ value }) => {
            await mutate({
                email: value.email,
                password: value.password,
            });
        },
    });

    const { mutate, isLoading } = useMutation(signInMutation, {
        onSuccess: ({ isSignedIn, nextStep }) => {
            setError(null);
            if (nextStep?.signInStep === "CONFIRM_SIGN_UP") {
                navigate("/signup/" + form.getFieldValue("email"));
            }

            if (isSignedIn) {
                const params = new URLSearchParams(window.location.search);
                const redirectUrl = params.get("redirect");
                navigate(redirectUrl || "/dashboard");
            }
        },
        onError: (error) => {
            if (error && getErrorMessage(error).endsWith("Incorrect username or password.")) {
                setError({ type: "auth" });
            } else if (error && getErrorMessage(error).includes("User does not exist")) {
                setError({ type: "userNotFound" });
            } else {
                setError({
                    type: "general",
                    message: getErrorMessage(error) || "An unexpected error occurred",
                });
            }
            console.error("Error signing in:", error);
        },
    });

    useEffect(() => {
        if (authStatus === "authenticated") {
            navigate("/dashboard");
        }
    }, [authStatus, navigate]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "radial-gradient(ellipse at top, rgba(66, 171, 155, 0.08) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(66, 171, 155, 0.05) 0%, transparent 50%)"
                        : "radial-gradient(ellipse at top, rgba(66, 171, 155, 0.12) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(230, 242, 239, 0.8) 0%, transparent 50%)",
            }}
        >
            {/* Header */}
            <Sheet
                sx={{
                    px: 3,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "transparent",
                }}
            >
                <Typography
                    level="h4"
                    component={NavLink}
                    to="/"
                    sx={{
                        fontWeight: 800,
                        textDecoration: "none",
                        background: "linear-gradient(135deg, #42AB9B 0%, #328D7F 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                    }}
                >
                    🦩 Stork
                </Typography>
                <ColorSchemeToggle />
            </Sheet>

            {/* Main Content */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                    py: 4,
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 420,
                        animation: "fadeInUp 0.5s ease-out",
                        "@keyframes fadeInUp": {
                            from: { opacity: 0, transform: "translateY(20px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                    }}
                >
                    <Sheet
                        variant="outlined"
                        sx={{
                            p: 4,
                            borderRadius: "xl",
                            boxShadow: "lg",
                            bgcolor: "background.surface",
                            [`& .${formLabelClasses.asterisk}`]: {
                                visibility: "hidden",
                            },
                        }}
                    >
                        <Stack gap={4}>
                            <Stack gap={1} sx={{ textAlign: "center" }}>
                                <Typography component="h1" level="h2" sx={{ fontWeight: 700 }}>
                                    Welcome back
                                </Typography>
                                <Typography level="body-md" textColor="text.secondary">
                                    Sign in to continue to your dashboard
                                </Typography>
                            </Stack>

                            {error?.type === "general" && (
                                <Typography level="body-sm" color="danger" sx={{ textAlign: "center" }}>
                                    {error.message}
                                </Typography>
                            )}

                            <form
                                onSubmit={async (event: React.FormEvent<SignInFormElement>) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    form.handleSubmit();
                                }}
                                style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                            >
                                <form.Field
                                    name="email"
                                    children={(field) => {
                                        return (
                                            <FormControl
                                                required
                                                error={
                                                    field.state.meta.touchedErrors.length > 0 ||
                                                    error?.type === "auth" ||
                                                    error?.type === "userNotFound"
                                                }
                                            >
                                                <FormLabel>Email</FormLabel>
                                                <Input
                                                    type="email"
                                                    size="lg"
                                                    value={field.state.value}
                                                    onChange={(e) => {
                                                        setError(null);
                                                        field.handleChange(e.target.value);
                                                    }}
                                                />
                                                <ValidationFieldInfo
                                                    field={field}
                                                    errorMessage={
                                                        error?.type === "userNotFound"
                                                            ? "User does not exist"
                                                            : error?.type === "auth"
                                                              ? "Incorrect username or password."
                                                              : ""
                                                    }
                                                />
                                            </FormControl>
                                        );
                                    }}
                                />
                                <form.Field
                                    name="password"
                                    children={(field) => {
                                        return (
                                            <FormControl
                                                required
                                                error={field.state.meta.touchedErrors.length > 0 || error?.type === "auth"}
                                            >
                                                <FormLabel>Password</FormLabel>
                                                <Input
                                                    type="password"
                                                    size="lg"
                                                    value={field.state.value}
                                                    onChange={(e) => {
                                                        field.handleChange(e.target.value);
                                                        setError(null);
                                                    }}
                                                />
                                                <ValidationFieldInfo
                                                    field={field}
                                                    errorMessage={error?.type === "auth" ? "Incorrect username or password." : ""}
                                                />
                                            </FormControl>
                                        );
                                    }}
                                />

                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Link level="body-sm" component={NavLink} to="/forgot-password">
                                        Forgot your password?
                                    </Link>
                                </Box>

                                <Button type="submit" size="lg" loading={isLoading} sx={{ mt: 1 }}>
                                    Sign in
                                </Button>
                            </form>

                            <Typography level="body-sm" sx={{ textAlign: "center" }}>
                                New to Stork?{" "}
                                <Link component={NavLink} to="/signup" fontWeight="lg">
                                    Create an account
                                </Link>
                            </Typography>
                        </Stack>
                    </Sheet>
                </Box>
            </Box>
        </Box>
    );
};
